import React from "react";
import { parseLocalDate } from "@ui/shared";

export type FlowBadge = "renewal" | "move-out" | "not-sure";

/**
 * MM/DD/YYYY. Parsed as local midnight (not `new Date(str)`, which reads
 * YYYY-MM-DD as UTC and shifts back a day in US timezones).
 */
const formatDateShort = (dateStr: string): string | null => {
  const d = parseLocalDate(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};

interface Props {
  /** Step labels in order, e.g. ["Contact", "Notes", "Payment", "Review"]. */
  steps: string[];
  /** 1-based index of the active step. */
  current: number;
  badge: FlowBadge;
  /**
   * Optional date shown under the badge as "Renews on: MM/DD/YYYY".
   * Pass a YYYY-MM-DD DATE string; rendered only when parseable.
   */
  renewsOn?: string | null;
}

const BADGE_LABEL: Record<FlowBadge, string> = {
  renewal: "Lease Renewal",
  "move-out": "Move-Out Notice",
  "not-sure": "Request a Renewal Offer",
};

// The wireframes colour-code each flow. Kept subtle here to sit inside the
// app's navy palette rather than the mockup's brighter blues.
const BADGE_CLASS: Record<FlowBadge, string> = {
  renewal: "bg-success/10 text-success",
  "move-out": "bg-danger/10 text-danger",
  "not-sure": "bg-warning/10 text-warning",
};

/**
 * Progress header for the renewal-concierge flows. Mirrors SDASteps'
 * visual language (numbered circles, checkmarks for completed steps) but
 * takes its labels as props since each of the three flows has a different
 * set of steps.
 */
const RenewalSteps: React.FC<Props> = ({
  steps,
  current,
  badge,
  renewsOn,
}) => (
  <div className="mb-6">
    <div className="mb-4 flex flex-col items-center gap-1">
      <span
        className={`rounded-full px-3 py-1 text-xs font-semibold ${BADGE_CLASS[badge]}`}
      >
        {BADGE_LABEL[badge]}
      </span>
      {renewsOn && formatDateShort(renewsOn) && (
        <span className="text-xs text-muted">
          Renews on: {formatDateShort(renewsOn)}
        </span>
      )}
    </div>

    <ol className="flex items-center gap-2 text-xs font-medium">
      {steps.map((label, idx) => {
        const id = idx + 1;
        const isActive = id === current;
        const isComplete = id < current;
        return (
          <React.Fragment key={label}>
            <li className="flex items-center gap-2">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${
                  isActive
                    ? "bg-primary text-white"
                    : isComplete
                      ? "bg-success text-white"
                      : "bg-gray-200 text-gray-500"
                }`}
              >
                {isComplete ? (
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  id
                )}
              </span>
              <span
                className={`hidden sm:inline ${
                  isActive
                    ? "text-primary"
                    : isComplete
                      ? "text-gray-700"
                      : "text-gray-400"
                }`}
              >
                {label}
              </span>
            </li>
            {idx < steps.length - 1 && (
              <li
                className={`h-px flex-1 ${
                  isComplete ? "bg-success" : "bg-gray-200"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  </div>
);

export default RenewalSteps;
